import SafeEmailPreview from '../common/SafeEmailPreview';
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Mail, ChevronDown, ChevronRight, FileText, AlertCircle, CheckCircle, Clock, Paperclip, FileJson, Copy, Check, Upload, Filter, Cpu, Award, GitCompare, Building2, Plane, Hotel, Car, AlertTriangle, CreditCard, Train, Briefcase, HelpCircle, MessageSquarePlus, X, Github, Activity } from 'lucide-react';
import {
  listParsingLogs,
  createParsingFeedbackFromLog,
  getParsingFeedbackGithubDiagnostic,
} from '../../services/emailAddressService';

// Error categories (must match backend ParsingErrorCategory enum)
const PARSING_FEEDBACK_ERROR_CATEGORIES = [
  { value: 'date_time', label: 'Date/Time' },
  { value: 'location', label: 'Location' },
  { value: 'flight_details', label: 'Flight Details' },
  { value: 'hotel_details', label: 'Hotel Details' },
  { value: 'amounts', label: 'Amounts/Pricing' },
  { value: 'contact_info', label: 'Contact Info' },
  { value: 'missing_data', label: 'Missing Data' },
  { value: 'extra_data', label: 'Extra/Wrong Data' },
  { value: 'format', label: 'Format Issue' },
  { value: 'other', label: 'Other' },
];

const PARSING_FEEDBACK_PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const ParsingLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(50);
  const [sourceFilter, setSourceFilter] = useState(''); // '', 'email', or 'document_import'
  const [copiedId, setCopiedId] = useState(null);

  // Selection state for creating parsing feedback from a log. Keyed by
  // log id; value is a Set of confirmation indexes that the user has
  // selected (checkboxes rendered next to each AI confirmation).
  const [selectedConfirmations, setSelectedConfirmations] = useState({}); // { [logId]: Set<number> }

  // Parsing feedback modal state (driven from the currently expanded log)
  const [feedbackModal, setFeedbackModal] = useState(null); // { log, indexes } | null
  const [feedbackForm, setFeedbackForm] = useState({
    description: '',
    priority: 'medium',
    categories: [],
    affectedFields: '',
    expectedValues: '',
  });
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');
  const [feedbackSuccess, setFeedbackSuccess] = useState(null); // null | { id, indexes }
  const [diagnosticRunning, setDiagnosticRunning] = useState(false);
  const [diagnosticResult, setDiagnosticResult] = useState(null); // null | { all_ok, checks }

  useEffect(() => {
    fetchLogs();
  }, [limit, sourceFilter]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { limit };
      if (sourceFilter) {
        params.source_type = sourceFilter;
      }
      const response = await listParsingLogs(params);
      if (response.success) {
        setLogs(response.logs || []);
        setTotal(response.total || 0);
      } else {
        setError('Failed to fetch parsing logs');
      }
    } catch (err) {
      console.error('Error fetching parsing logs:', err);
      setError(err.message || 'Failed to fetch parsing logs');
    } finally {
      setLoading(false);
    }
  };

  const toggleLogExpansion = (logId) => {
    setExpandedLogId(expandedLogId === logId ? null : logId);
  };

  const toggleSection = (logId, section) => {
    const key = `${logId}-${section}`;
    setExpandedSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isSectionExpanded = (logId, section, defaultExpanded = false) => {
    const key = `${logId}-${section}`;
    // Only use the default when the user has never interacted with this
    // section; once they click it, honour their explicit choice so they
    // can still collapse a default-expanded section.
    if (expandedSections[key] !== undefined) {
      return expandedSections[key];
    }
    return defaultExpanded;
  };

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // ---- Parsing feedback selection helpers ----

  const getSelectedIndexesForLog = (logId) => {
    const set = selectedConfirmations[logId];
    return set ? Array.from(set).sort((a, b) => a - b) : [];
  };

  const toggleConfirmationSelection = (logId, index) => {
    setSelectedConfirmations((prev) => {
      const current = new Set(prev[logId] || []);
      if (current.has(index)) {
        current.delete(index);
      } else {
        current.add(index);
      }
      return { ...prev, [logId]: current };
    });
  };

  const clearSelectionForLog = (logId) => {
    setSelectedConfirmations((prev) => {
      const next = { ...prev };
      delete next[logId];
      return next;
    });
  };

  const openFeedbackModal = (log) => {
    const indexes = getSelectedIndexesForLog(log.id);
    if (indexes.length === 0) return;
    setFeedbackForm({
      description: '',
      priority: 'medium',
      categories: [],
      affectedFields: '',
      expectedValues: '',
    });
    setFeedbackError('');
    setFeedbackSuccess(null);
    setFeedbackModal({ log, indexes });
  };

  const closeFeedbackModal = () => {
    setFeedbackModal(null);
    setFeedbackError('');
    setFeedbackSuccess(null);
  };

  const runGithubDiagnostic = async () => {
    setDiagnosticRunning(true);
    setDiagnosticResult(null);
    try {
      const result = await getParsingFeedbackGithubDiagnostic();
      setDiagnosticResult(result);
    } catch (err) {
      console.error('GitHub diagnostic failed:', err);
      setDiagnosticResult({
        all_ok: false,
        error: err.response?.data?.detail || err.message || 'Diagnostic request failed',
      });
    } finally {
      setDiagnosticRunning(false);
    }
  };

  const toggleFeedbackCategory = (value) => {
    setFeedbackForm((prev) => {
      const has = prev.categories.includes(value);
      return {
        ...prev,
        categories: has
          ? prev.categories.filter((c) => c !== value)
          : [...prev.categories, value],
      };
    });
  };

  const submitFeedbackFromLog = async () => {
    if (!feedbackModal) return;
    setFeedbackError('');

    if (!feedbackForm.description.trim()) {
      setFeedbackError('Please describe the issue.');
      return;
    }

    const affectedFields = feedbackForm.affectedFields
      .split(',')
      .map((f) => f.trim())
      .filter(Boolean);

    let expectedValues = null;
    if (feedbackForm.expectedValues.trim()) {
      try {
        expectedValues = JSON.parse(feedbackForm.expectedValues);
      } catch {
        expectedValues = { user_provided: feedbackForm.expectedValues.trim() };
      }
    }

    const payload = {
      confirmation_indexes: feedbackModal.indexes,
      error_categories: feedbackForm.categories,
      affected_fields: affectedFields,
      user_description: feedbackForm.description.trim(),
      expected_values: expectedValues,
      priority: feedbackForm.priority,
    };

    setFeedbackSubmitting(true);
    try {
      const result = await createParsingFeedbackFromLog(feedbackModal.log.id, payload);
      setFeedbackSuccess({
        id: result.id,
        indexes: feedbackModal.indexes,
        github: result.github || null,
      });
      // Clear the selection for this log so the button disables itself
      clearSelectionForLog(feedbackModal.log.id);
    } catch (err) {
      console.error('Failed to create parsing feedback from log:', err);
      setFeedbackError(
        err.response?.data?.detail || 'Failed to create parsing feedback entry.'
      );
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  const renderFeedbackModal = () => {
    if (!feedbackModal) return null;

    const { log, indexes } = feedbackModal;

    return createPortal(
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] overflow-y-auto py-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <MessageSquarePlus className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Create Parsing Feedback</h2>
                  <p className="text-sm text-gray-500">
                    {indexes.length === 1
                      ? '1 confirmation selected'
                      : `${indexes.length} confirmations selected`}
                    {log.subject ? ` · ${log.subject.substring(0, 60)}${log.subject.length > 60 ? '…' : ''}` : ''}
                  </p>
                </div>
              </div>
              <button
                onClick={closeFeedbackModal}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-4 sm:p-6 space-y-5">
            {feedbackSuccess ? (
              <div className="space-y-4">
                <div className="flex items-start gap-2 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-green-800">Feedback saved</div>
                    <div className="text-xs text-green-700 mt-1">
                      Entry <code className="bg-white px-1 rounded">{feedbackSuccess.id}</code> created in the parsing_feedback collection.
                    </div>
                  </div>
                </div>

                {feedbackSuccess.github && feedbackSuccess.github.committed ? (
                  <div className="flex items-start gap-2 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <div className="font-medium text-emerald-800">
                        Committed to parsing-feedback repo
                      </div>
                      <div className="text-xs text-emerald-700 mt-1 break-all">
                        commit{' '}
                        <code className="bg-white px-1 rounded">
                          {(feedbackSuccess.github.commit_sha || '').substring(0, 10)}
                        </code>
                      </div>
                      {feedbackSuccess.github.paths && (
                        <ul className="text-xs text-emerald-700 mt-2 list-disc list-inside space-y-0.5 break-all">
                          {Object.entries(feedbackSuccess.github.paths).map(([k, v]) => (
                            <li key={k}>
                              <code className="bg-white px-1 rounded">{v}</code>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                ) : feedbackSuccess.github ? (
                  <div className="flex items-start gap-2 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <div className="font-medium text-yellow-800">
                        Saved to database, but not committed to GitHub
                      </div>
                      <div className="text-xs text-yellow-700 mt-1 break-words">
                        {feedbackSuccess.github.skip_reason === 'github_commit_disabled' && (
                          <>GitHub commit is disabled in system settings. Enable it under Parsing Feedback settings, then re-send from the admin page.</>
                        )}
                        {feedbackSuccess.github.skip_reason === 'github_token_missing' && (
                          <>The backend is missing the <code>GITHUB_TOKEN</code> environment variable. Configure it and re-send from the admin page.</>
                        )}
                        {feedbackSuccess.github.error && (
                          <>GitHub commit failed: <code className="bg-white px-1 rounded">{feedbackSuccess.github.error}</code></>
                        )}
                        {!feedbackSuccess.github.skip_reason && !feedbackSuccess.github.error && (
                          <>GitHub commit did not complete. The entry is saved in the database and can be retried from the Parsing Feedback admin page.</>
                        )}
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="flex justify-end">
                  <button
                    onClick={closeFeedbackModal}
                    className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 text-sm font-medium"
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <>
                {feedbackError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                    {feedbackError}
                  </div>
                )}

                <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600 space-y-1">
                  <div>
                    <span className="font-medium">Parsing log:</span>{' '}
                    <code className="bg-white px-1 rounded">{log.id}</code>
                  </div>
                  {log.from_email && (
                    <div>
                      <span className="font-medium">From:</span> {log.from_email}
                    </div>
                  )}
                  {log.ai_model && (
                    <div>
                      <span className="font-medium">AI model:</span> {log.ai_model}
                    </div>
                  )}
                  <div>
                    <span className="font-medium">Includes:</span> selected AI confirmations,
                    AwardWallet confirmations, parsing comparison, originator, raw email.
                  </div>
                </div>

                {/* GitHub connectivity diagnostic */}
                <div className="border border-gray-200 rounded-lg p-3 text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-gray-700">
                      <Github className="w-4 h-4" />
                      <span className="font-medium">GitHub commit target:</span>
                      <code className="bg-gray-100 px-1 rounded">urbanrideinc/Eatravel-2.0</code>
                      <span className="text-gray-500">→</span>
                      <code className="bg-gray-100 px-1 rounded">parsing-feedback/</code>
                    </div>
                    <button
                      type="button"
                      onClick={runGithubDiagnostic}
                      disabled={diagnosticRunning}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium whitespace-nowrap disabled:opacity-60"
                    >
                      <Activity className="w-3.5 h-3.5" />
                      {diagnosticRunning ? 'Checking…' : 'Test GitHub connection'}
                    </button>
                  </div>
                  {diagnosticResult && (
                    <div className="mt-3 space-y-1">
                      {diagnosticResult.error ? (
                        <div className="text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                          {diagnosticResult.error}
                        </div>
                      ) : (
                        <>
                          <div
                            className={`font-medium ${
                              diagnosticResult.all_ok ? 'text-green-700' : 'text-red-700'
                            }`}
                          >
                            {diagnosticResult.all_ok
                              ? 'All GitHub checks passed'
                              : 'One or more checks failed — see below'}
                          </div>
                          <ul className="space-y-0.5 font-mono break-all">
                            {Object.entries(diagnosticResult.checks || {}).map(([name, info]) => {
                              const ok = info && info.ok;
                              return (
                                <li
                                  key={name}
                                  className={ok ? 'text-green-700' : 'text-red-700'}
                                >
                                  <span className="inline-block w-3 text-center">
                                    {ok ? '✓' : '✗'}
                                  </span>{' '}
                                  {name}
                                  {info && info.error && (
                                    <span className="text-gray-600"> — {info.error}</span>
                                  )}
                                  {info && info.login && (
                                    <span className="text-gray-600"> — {info.login}</span>
                                  )}
                                  {info && info.default_branch && (
                                    <span className="text-gray-600">
                                      {' '}— default_branch={info.default_branch}
                                    </span>
                                  )}
                                  {info && info.prefix && (
                                    <span className="text-gray-600">
                                      {' '}— prefix={info.prefix} length={info.length}
                                    </span>
                                  )}
                                  {info && info.had_whitespace && (
                                    <span className="text-amber-700">
                                      {' '}— trailing whitespace was stripped before use
                                    </span>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        </>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Describe the issue <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={feedbackForm.description}
                    onChange={(e) =>
                      setFeedbackForm({ ...feedbackForm, description: e.target.value })
                    }
                    placeholder="What should the parser have extracted? e.g. 'Flight number 1632 was missed; AW got it right.'"
                    rows={4}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Error categories
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {PARSING_FEEDBACK_ERROR_CATEGORIES.map((cat) => (
                      <button
                        key={cat.value}
                        type="button"
                        onClick={() => toggleFeedbackCategory(cat.value)}
                        className={`px-3 py-1.5 rounded-full text-xs transition-colors ${
                          feedbackForm.categories.includes(cat.value)
                            ? 'bg-amber-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Affected fields (comma-separated, optional)
                  </label>
                  <input
                    type="text"
                    value={feedbackForm.affectedFields}
                    onChange={(e) =>
                      setFeedbackForm({ ...feedbackForm, affectedFields: e.target.value })
                    }
                    placeholder="flight_number, departure_time"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expected values (optional, JSON or free text)
                  </label>
                  <textarea
                    value={feedbackForm.expectedValues}
                    onChange={(e) =>
                      setFeedbackForm({ ...feedbackForm, expectedValues: e.target.value })
                    }
                    placeholder='{"flight_number": "1632"}'
                    rows={2}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono focus:ring-amber-500 focus:border-amber-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {PARSING_FEEDBACK_PRIORITIES.map((p) => (
                      <button
                        key={p.value}
                        type="button"
                        onClick={() => setFeedbackForm({ ...feedbackForm, priority: p.value })}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                          feedbackForm.priority === p.value
                            ? 'bg-amber-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={closeFeedbackModal}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
                    disabled={feedbackSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={submitFeedbackFromLog}
                    className="px-6 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 text-sm font-medium disabled:opacity-50"
                    disabled={feedbackSubmitting}
                  >
                    {feedbackSubmitting ? 'Saving…' : 'Save Feedback Entry'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>,
      document.body
    );
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3" />
            Completed
          </span>
        );
      case 'processing':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <Clock className="w-3 h-3" />
            Processing
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <AlertCircle className="w-3 h-3" />
            Failed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {status || 'Unknown'}
          </span>
        );
    }
  };

  const getSourceBadge = (log) => {
    if (log.source_type === 'document_import') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
          <Upload className="w-3 h-3" />
          Document Import
        </span>
      );
    } else {
      // Email source
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          {log.email_type === 'trip' ? 'Trip Email' : 'Executive Email'}
        </span>
      );
    }
  };

  const getAiModelBadge = (log) => {
    if (!log.ai_model) return null;

    // Determine color based on provider
    const isGemini = log.ai_provider === 'gemini' || log.ai_model?.startsWith('gemini');
    const isClaude = log.ai_provider === 'claude' || log.ai_model?.startsWith('claude');

    // Format model name for display (shorten if needed)
    const formatModelName = (model) => {
      if (!model) return 'Unknown';
      // Shorten common model names
      if (model.includes('claude-sonnet-5-5')) return 'Claude Sonnet 5.5';
      if (model.includes('claude-haiku-5-5')) return 'Claude Haiku 5.5';
      if (model.includes('claude-opus-5-5')) return 'Claude Opus 5.5';
      if (model.includes('claude-fable-5-1')) return 'Claude Fable 5.1';
      if (model.includes('claude-haiku-4-5')) return 'Claude Haiku 4.5';
      if (model.includes('claude-sonnet-4-6')) return 'Claude Sonnet 4.6';
      if (model.includes('gemini-3.8-flash')) return 'Gemini 3.8 Flash';
      if (model.includes('gemini-3.6-flash')) return 'Gemini 3.6 Flash';
      if (model.includes('gemini-3.5-flash-lite')) return 'Gemini 3.5 Flash-Lite';
      if (model.includes('gemini-2.0-flash-exp')) return 'Gemini 2.0 Exp';
      if (model.includes('gemini-2.0-flash')) return 'Gemini 2.0';
      if (model.includes('gemini-2.5-flash')) return 'Gemini 2.5';
      if (model.includes('gemini-1.5-flash')) return 'Gemini 1.5';
      if (model.includes('gemini-2.5-pro')) return 'Gemini 2.5 Pro';
      if (model.includes('claude-sonnet-4')) return 'Claude Sonnet 4';
      if (model.includes('claude-opus-4')) return 'Claude Opus 4';
      if (model.includes('claude-3-5-sonnet')) return 'Claude 3.5 Sonnet';
      if (model.includes('claude-3-5-haiku')) return 'Claude 3.5 Haiku';
      // Return truncated version for unknown models
      return model.length > 20 ? model.substring(0, 17) + '...' : model;
    };

    if (isClaude) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700" title={log.ai_model}>
          <Cpu className="w-3 h-3" />
          {formatModelName(log.ai_model)}
        </span>
      );
    } else if (isGemini) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700" title={log.ai_model}>
          <Cpu className="w-3 h-3" />
          {formatModelName(log.ai_model)}
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700" title={log.ai_model}>
          <Cpu className="w-3 h-3" />
          {formatModelName(log.ai_model)}
        </span>
      );
    }
  };

  const getAwardWalletBadge = (log) => {
    if (!log.awardwallet_parsing) return null;

    const aw = log.awardwallet_parsing;
    const success = aw.success;
    const count = aw.confirmations_count || 0;
    const error = aw.error;

    if (error) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700" title={`AwardWallet Error: ${error}`}>
          <Award className="w-3 h-3" />
          AW Error
        </span>
      );
    }

    if (success) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700" title={`AwardWallet found ${count} confirmation(s)`}>
          <Award className="w-3 h-3" />
          AW: {count}
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600" title="AwardWallet parsing ran but found nothing">
        <Award className="w-3 h-3" />
        AW: 0
      </span>
    );
  };

  const getComparisonBadge = (log) => {
    if (!log.awardwallet_parsing?.comparison_metrics) return null;

    const metrics = log.awardwallet_parsing.comparison_metrics;

    if (metrics.both_parsed) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700" title="Both AI and AwardWallet parsed successfully">
          <GitCompare className="w-3 h-3" />
          Both
        </span>
      );
    } else if (metrics.ai_only) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700" title="Only AI parser found results">
          <GitCompare className="w-3 h-3" />
          AI Only
        </span>
      );
    } else if (metrics.aw_only) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700" title="Only AwardWallet found results">
          <GitCompare className="w-3 h-3" />
          AW Only
        </span>
      );
    }

    return null;
  };

  const getOriginatorBadge = (log) => {
    if (!log.document_originator) return null;

    const { sender_name, category } = log.document_originator;
    if (!sender_name && (!category || category === 'OTHER')) return null;

    // New category colors and icons
    const categoryConfig = {
      CREDIT_CARD: { color: 'bg-amber-100 text-amber-700', icon: CreditCard, label: 'Credit Card' },
      AIRLINES: { color: 'bg-sky-100 text-sky-700', icon: Plane, label: 'Airlines' },
      HOTELS: { color: 'bg-violet-100 text-violet-700', icon: Hotel, label: 'Hotels' },
      CAR_RENTALS: { color: 'bg-emerald-100 text-emerald-700', icon: Car, label: 'Car Rentals' },
      TRAINS: { color: 'bg-orange-100 text-orange-700', icon: Train, label: 'Trains' },
      TRAVEL_AGENCIES: { color: 'bg-rose-100 text-rose-700', icon: Briefcase, label: 'Travel Agency' },
      OTHER: { color: 'bg-gray-100 text-gray-600', icon: HelpCircle, label: 'Other' },
      // Legacy mappings for old data
      airline: { color: 'bg-sky-100 text-sky-700', icon: Plane, label: 'Airlines' },
      hotel: { color: 'bg-violet-100 text-violet-700', icon: Hotel, label: 'Hotels' },
      car_rental: { color: 'bg-emerald-100 text-emerald-700', icon: Car, label: 'Car Rentals' },
      travel_agency: { color: 'bg-rose-100 text-rose-700', icon: Briefcase, label: 'Travel Agency' },
      unknown: { color: 'bg-gray-100 text-gray-600', icon: HelpCircle, label: 'Unknown' }
    };

    const config = categoryConfig[category] || categoryConfig.OTHER;
    const Icon = config.icon;
    const displayName = sender_name || config.label;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`} title={`Document originator: ${displayName} (${config.label})`}>
        <Icon className="w-3 h-3" />
        {displayName}
      </span>
    );
  };

  const getParsingComparisonBadge = (log) => {
    if (!log.parsing_comparison) return null;

    const { match_percentage, mismatched_fields } = log.parsing_comparison;
    const hasMismatches = mismatched_fields && mismatched_fields.length > 0;

    if (hasMismatches) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700" title={`${mismatched_fields.length} field mismatches detected`}>
          <AlertTriangle className="w-3 h-3" />
          {mismatched_fields.length} mismatch{mismatched_fields.length !== 1 ? 'es' : ''}
        </span>
      );
    }

    if (match_percentage === 100) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700" title="AI and AwardWallet results match 100%">
          <CheckCircle className="w-3 h-3" />
          100% Match
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700" title={`${match_percentage}% field match between AI and AwardWallet`}>
        <GitCompare className="w-3 h-3" />
        {match_percentage}% Match
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatEmailBody = (rawEmailData) => {
    if (!rawEmailData) return null;

    // Prefer HTML, fall back to text
    const html = rawEmailData.html;
    const text = rawEmailData.text;

    if (html) {
      return { type: 'html', content: html };
    } else if (text) {
      return { type: 'text', content: text };
    }
    return null;
  };

  const renderCollapsibleSection = (logId, title, icon, children, defaultExpanded = false) => {
    const isExpanded = isSectionExpanded(logId, title, defaultExpanded);

    return (
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => toggleSection(logId, title)}
          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            {icon}
            <span className="font-medium text-sm text-gray-700">{title}</span>
          </div>
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500" />
          )}
        </button>
        {isExpanded && (
          <div className="p-4 bg-white">
            {children}
          </div>
        )}
      </div>
    );
  };

  const renderJsonViewer = (data, copyId) => {
    if (!data) return <p className="text-gray-500 text-sm">No data available</p>;

    const jsonString = JSON.stringify(data, null, 2);

    return (
      <div className="relative">
        <button
          onClick={() => copyToClipboard(jsonString, copyId)}
          className="absolute top-2 right-2 p-1.5 rounded bg-gray-100 hover:bg-gray-200 transition-colors"
          title="Copy JSON"
        >
          {copiedId === copyId ? (
            <Check className="w-4 h-4 text-green-600" />
          ) : (
            <Copy className="w-4 h-4 text-gray-600" />
          )}
        </button>
        <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-xs overflow-x-auto max-h-96">
          {jsonString}
        </pre>
      </div>
    );
  };

  const renderEmailBody = (rawEmailData, logId) => {
    const body = formatEmailBody(rawEmailData);

    if (!body) {
      return <p className="text-gray-500 text-sm">No email body content available</p>;
    }

    if (body.type === 'html') {
      return (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">HTML Content</span>
            <button
              onClick={() => copyToClipboard(body.content, `html-${logId}`)}
              className="p-1.5 rounded bg-gray-100 hover:bg-gray-200 transition-colors"
              title="Copy HTML"
            >
              {copiedId === `html-${logId}` ? (
                <Check className="w-4 h-4 text-green-600" />
              ) : (
                <Copy className="w-4 h-4 text-gray-600" />
              )}
            </button>
          </div>
          <SafeEmailPreview html={body.content} />
          <details className="mt-2">
            <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
              View raw HTML source
            </summary>
            <pre className="mt-2 bg-gray-900 text-gray-100 p-4 rounded-lg text-xs overflow-x-auto max-h-64">
              {body.content}
            </pre>
          </details>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">Plain Text Content</span>
          <button
            onClick={() => copyToClipboard(body.content, `text-${logId}`)}
            className="p-1.5 rounded bg-gray-100 hover:bg-gray-200 transition-colors"
            title="Copy Text"
          >
            {copiedId === `text-${logId}` ? (
              <Check className="w-4 h-4 text-green-600" />
            ) : (
              <Copy className="w-4 h-4 text-gray-600" />
            )}
          </button>
        </div>
        <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm whitespace-pre-wrap max-h-96 overflow-auto">
          {body.content}
        </pre>
      </div>
    );
  };

  const renderLogHeader = (log) => {
    const isEmail = log.source_type === 'email' || !log.source_type;
    const isDocumentImport = log.source_type === 'document_import';

    return (
      <div className="flex-1 min-w-0">
        {/* Status, Source, AI Model, and AwardWallet Badges */}
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          {getStatusBadge(log.processing_status)}
          {getSourceBadge(log)}
          {getOriginatorBadge(log)}
          {getAiModelBadge(log)}
          {getAwardWalletBadge(log)}
          {getComparisonBadge(log)}
          {getParsingComparisonBadge(log)}
          {log.attachment_count > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
              <Paperclip className="w-3 h-3" />
              {log.attachment_count} attachment{log.attachment_count !== 1 ? 's' : ''}
            </span>
          )}
          {log.body_confirmations_found > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
              <FileText className="w-3 h-3" />
              {log.body_confirmations_found} from body
            </span>
          )}
          {log.duplicates_skipped > 0 && (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
              {log.duplicates_skipped} duplicate{log.duplicates_skipped !== 1 ? 's' : ''} skipped
            </span>
          )}
          {(log.ai_parsed_confirmations || log.parsed_confirmations)?.length > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
              <Cpu className="w-3 h-3" />
              AI: {(log.ai_parsed_confirmations || log.parsed_confirmations).length}
            </span>
          )}
          {log.awardwallet_parsed_confirmations?.length > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
              <Award className="w-3 h-3" />
              AW: {log.awardwallet_parsed_confirmations.length}
            </span>
          )}
        </div>

        {/* Title/Subject */}
        <h3 className="text-sm font-medium text-gray-900 truncate mb-1">
          {isDocumentImport
            ? log.original_filename || '(Unnamed document)'
            : log.subject || '(No subject)'}
        </h3>

        {/* Details based on source type */}
        <div className="text-xs text-gray-500 space-y-0.5">
          {isEmail && (
            <>
              <p>
                <span className="font-medium">From:</span> {log.from_name ? `${log.from_name} <${log.from_email}>` : log.from_email}
              </p>
              <p>
                <span className="font-medium">To:</span> {log.to_email}
              </p>
            </>
          )}
          {isDocumentImport && (
            <>
              <p>
                <span className="font-medium">Document Type:</span> {log.document_type?.replace('_', ' ') || 'Unknown'}
              </p>
              <p>
                <span className="font-medium">Imported By:</span> {log.imported_by_name || 'Unknown'}
              </p>
              {log.trip_name && (
                <p>
                  <span className="font-medium">Trip:</span> {log.trip_name}
                </p>
              )}
            </>
          )}
          <p>
            <span className="font-medium">{isDocumentImport ? 'Imported:' : 'Received:'}</span> {formatDate(log.received_at)}
          </p>
        </div>

        {/* Results Summary */}
        {log.processing_status === 'completed' && (
          <div className="mt-2 text-xs">
            <span className="text-green-600 font-medium">
              Created {log.created_components?.length || 0} component{(log.created_components?.length || 0) !== 1 ? 's' : ''}
            </span>
            {log.attachments_parsed > 0 && (
              <span className="text-gray-500 ml-2">
                ({log.attachments_parsed} attachment{log.attachments_parsed !== 1 ? 's' : ''} parsed)
              </span>
            )}
          </div>
        )}

        {/* Error Message */}
        {log.error_message && (
          <div className="mt-2 text-xs text-red-600">
            Error: {log.error_message}
          </div>
        )}
      </div>
    );
  };

  const renderExpandedDetails = (log) => {
    const isEmail = log.source_type === 'email' || !log.source_type;
    const isDocumentImport = log.source_type === 'document_import';

    const confirmationsForLog = log.ai_parsed_confirmations || log.parsed_confirmations || [];
    const selectedCountForLog = (selectedConfirmations[log.id] || new Set()).size;

    return (
      <div className="px-4 pb-4 space-y-4 border-t border-gray-100 bg-gray-50">
        <div className="pt-4 space-y-4">
          {/* Parsing feedback action bar (always visible at top of expanded log) */}
          {confirmationsForLog.length > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-amber-50 border border-amber-300 rounded-lg px-4 py-3">
              <div className="flex items-start gap-2 text-sm text-amber-900">
                <MessageSquarePlus className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-700" />
                <div>
                  <div className="font-medium">Create parsing feedback entry</div>
                  <div className="text-xs text-amber-800 mt-0.5">
                    Tick one or more AI-parsed confirmations below (they're shown in the
                    "AI Parsed Confirmations (JSON)" section). The saved entry will include
                    the selected AI output, the AwardWallet output, the parsing comparison,
                    and the raw email — everything needed to improve the parser.
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => openFeedbackModal(log)}
                disabled={selectedCountForLog === 0}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap self-start sm:self-auto ${
                  selectedCountForLog === 0
                    ? 'bg-amber-200 text-amber-500 cursor-not-allowed'
                    : 'bg-amber-600 text-white hover:bg-amber-700'
                }`}
              >
                <MessageSquarePlus className="w-4 h-4" />
                {selectedCountForLog === 0
                  ? 'Select confirmations below'
                  : `Create Feedback (${selectedCountForLog})`}
              </button>
            </div>
          )}

          {/* Original Email Content (email only) */}
          {isEmail && renderCollapsibleSection(
            log.id,
            'Original Email Content',
            <Mail className="w-4 h-4 text-blue-600" />,
            renderEmailBody(log.raw_email_data, log.id)
          )}

          {/* Email Metadata (email only) */}
          {isEmail && renderCollapsibleSection(
            log.id,
            'Email Metadata (Headers)',
            <FileText className="w-4 h-4 text-gray-600" />,
            renderJsonViewer(
              log.raw_email_data ? {
                to: log.raw_email_data.to,
                from: log.raw_email_data.from,
                from_name: log.raw_email_data.from_name,
                subject: log.raw_email_data.subject,
                attachment_count: log.raw_email_data.attachment_count,
                attachment_info: log.raw_email_data.attachment_info
              } : null,
              `metadata-${log.id}`
            )
          )}

          {/* Document Import Info (document import only) */}
          {isDocumentImport && renderCollapsibleSection(
            log.id,
            'Import Details',
            <Upload className="w-4 h-4 text-orange-600" />,
            <div className="space-y-2 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="font-medium text-gray-700">File Name:</span>
                  <p className="text-gray-600">{log.original_filename || 'N/A'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Document Type:</span>
                  <p className="text-gray-600 capitalize">{log.document_type?.replace('_', ' ') || 'N/A'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Imported By:</span>
                  <p className="text-gray-600">{log.imported_by_name || 'N/A'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Document ID:</span>
                  <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">{log.document_id || 'N/A'}</code>
                </div>
              </div>
            </div>
          )}

          {/* Processing Results */}
          {renderCollapsibleSection(
            log.id,
            'Processing Results',
            <FileJson className="w-4 h-4 text-green-600" />,
            <div className="space-y-4">
              <div className={`grid ${isEmail ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-2'} gap-4 text-sm`}>
                {isEmail && (
                  <>
                    <div className="bg-blue-50 rounded-lg p-3">
                      <div className="text-blue-600 font-medium">Attachments</div>
                      <div className="text-2xl font-bold text-blue-900">{log.attachment_count || 0}</div>
                      <div className="text-xs text-blue-600">{log.attachments_parsed || 0} parsed</div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3">
                      <div className="text-purple-600 font-medium">Body Parsed</div>
                      <div className="text-2xl font-bold text-purple-900">{log.body_parsed ? 'Yes' : 'No'}</div>
                      <div className="text-xs text-purple-600">{log.body_confirmations_found || 0} found</div>
                    </div>
                  </>
                )}
                <div className="bg-green-50 rounded-lg p-3">
                  <div className="text-green-600 font-medium">Components</div>
                  <div className="text-2xl font-bold text-green-900">{log.created_components?.length || 0}</div>
                  <div className="text-xs text-green-600">created</div>
                </div>
                {isEmail && (
                  <div className="bg-yellow-50 rounded-lg p-3">
                    <div className="text-yellow-600 font-medium">Duplicates</div>
                    <div className="text-2xl font-bold text-yellow-900">{log.duplicates_skipped || 0}</div>
                    <div className="text-xs text-yellow-600">skipped</div>
                  </div>
                )}
              </div>

              {/* Created Component IDs */}
              {log.created_components && log.created_components.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Created Component IDs:</h4>
                  <div className="flex flex-wrap gap-2">
                    {log.created_components.map((compId, idx) => (
                      <code key={idx} className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">
                        {compId}
                      </code>
                    ))}
                  </div>
                </div>
              )}

              {/* Created Document IDs */}
              {log.created_documents && log.created_documents.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Created Document IDs:</h4>
                  <div className="flex flex-wrap gap-2">
                    {log.created_documents.map((docId, idx) => (
                      <code key={idx} className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">
                        {docId}
                      </code>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Document Originator */}
          {log.document_originator && renderCollapsibleSection(
            log.id,
            'Document Originator',
            <Building2 className="w-4 h-4 text-teal-600" />,
            <div className="space-y-3">
              {/* Forwarded Email Warning */}
              {log.document_originator.is_forwarded && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-sm font-medium text-yellow-800">Forwarded Email Detected</div>
                    {log.document_originator.original_sender && (
                      <div className="text-xs text-yellow-700 mt-1">
                        Original sender: {log.document_originator.original_sender}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-xs text-gray-500 mb-1">Category</div>
                  <div className="flex items-center gap-1.5">
                    {log.document_originator.category === 'CREDIT_CARD' && <CreditCard className="w-4 h-4 text-amber-500" />}
                    {(log.document_originator.category === 'AIRLINES' || log.document_originator.category === 'airline') && <Plane className="w-4 h-4 text-sky-500" />}
                    {(log.document_originator.category === 'HOTELS' || log.document_originator.category === 'hotel') && <Hotel className="w-4 h-4 text-violet-500" />}
                    {(log.document_originator.category === 'CAR_RENTALS' || log.document_originator.category === 'car_rental') && <Car className="w-4 h-4 text-emerald-500" />}
                    {log.document_originator.category === 'TRAINS' && <Train className="w-4 h-4 text-orange-500" />}
                    {(log.document_originator.category === 'TRAVEL_AGENCIES' || log.document_originator.category === 'travel_agency') && <Briefcase className="w-4 h-4 text-rose-500" />}
                    {(log.document_originator.category === 'OTHER' || log.document_originator.category === 'unknown') && <HelpCircle className="w-4 h-4 text-gray-500" />}
                    <span className="text-sm font-medium text-gray-900">
                      {log.document_originator.category?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown'}
                    </span>
                  </div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-xs text-gray-500 mb-1">Sender Name</div>
                  <div className="text-sm font-medium text-gray-900">
                    {log.document_originator.sender_name || 'Unknown'}
                  </div>
                </div>
                {log.document_originator.confidence !== undefined && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1">Confidence</div>
                    <div className={`text-sm font-medium ${
                      log.document_originator.confidence >= 0.8 ? 'text-green-600' :
                      log.document_originator.confidence >= 0.5 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {Math.round(log.document_originator.confidence * 100)}%
                    </div>
                  </div>
                )}
                {/* Legacy fields for backward compatibility */}
                {log.document_originator.sender_type && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1">Sender Type</div>
                    <div className="text-sm font-medium text-gray-900 capitalize">
                      {log.document_originator.sender_type?.replace('_', ' ') || 'Unknown'}
                    </div>
                  </div>
                )}
                {log.document_originator.carrier_code && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1">Carrier Code</div>
                    <div className="text-sm font-medium text-gray-900 font-mono">
                      {log.document_originator.carrier_code}
                    </div>
                  </div>
                )}
                {log.document_originator.sender_pattern && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1">Domain Pattern</div>
                    <div className="text-sm font-medium text-gray-900 font-mono">
                      {log.document_originator.sender_pattern}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* AI Parsed Confirmations (JSON Output) */}
          {renderCollapsibleSection(
            log.id,
            'AI Parsed Confirmations (JSON)',
            <Cpu className="w-4 h-4 text-indigo-600" />,
            (() => {
              const confirmations = log.ai_parsed_confirmations || log.parsed_confirmations || [];
              const selectedForLog = selectedConfirmations[log.id] || new Set();

              return (
                <div className="space-y-4">
                  {confirmations.length > 0 && (
                    <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      <span className="font-medium">Tip:</span>{' '}
                      tick one or more confirmations below, then use the
                      <span className="font-medium"> &quot;Create Feedback&quot;</span>{' '}
                      button at the top of this log to save a parsing feedback entry.
                    </div>
                  )}
                  {confirmations.length > 0 ? (
                    confirmations.map((conf, idx) => {
                      const checked = selectedForLog.has(idx);
                      return (
                        <div
                          key={idx}
                          className={`border rounded-lg overflow-hidden ${
                            checked ? 'border-amber-400 ring-1 ring-amber-400' : 'border-gray-200'
                          }`}
                        >
                          <div className="bg-indigo-50 px-4 py-2 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleConfirmationSelection(log.id, idx)}
                                className="h-4 w-4 text-amber-600 border-gray-300 rounded cursor-pointer"
                                title="Select for parsing feedback"
                              />
                              <span className="px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                                AI Parser
                              </span>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                conf.source === 'attachment'
                                  ? 'bg-blue-100 text-blue-800'
                                  : conf.source === 'document_import'
                                  ? 'bg-orange-100 text-orange-800'
                                  : 'bg-purple-100 text-purple-800'
                              }`}>
                                {conf.source === 'attachment' ? 'From Attachment'
                                  : conf.source === 'document_import' ? 'Document Import'
                                  : 'From Email Body'}
                              </span>
                              <span className="text-sm font-medium text-gray-700">
                                {conf.data?.document_type || 'Unknown Type'}
                              </span>
                              {conf.data?.confirmation_number && (
                                <span className="text-xs text-gray-500">
                                  #{conf.data.confirmation_number}
                                </span>
                              )}
                            </div>
                            <button
                              onClick={() => copyToClipboard(JSON.stringify(conf.data, null, 2), `ai-conf-${log.id}-${idx}`)}
                              className="p-1.5 rounded bg-white hover:bg-gray-200 transition-colors"
                              title="Copy JSON"
                            >
                              {copiedId === `ai-conf-${log.id}-${idx}` ? (
                                <Check className="w-4 h-4 text-green-600" />
                              ) : (
                                <Copy className="w-4 h-4 text-gray-600" />
                              )}
                            </button>
                          </div>
                          <pre className="bg-gray-900 text-gray-100 p-4 text-xs overflow-x-auto max-h-64">
                            {JSON.stringify(conf.data, null, 2)}
                          </pre>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-gray-500 text-sm">No confirmations were parsed by AI</p>
                  )}
                </div>
              );
            })(),
            true  // defaultExpanded: make the feedback selection UI discoverable
          )}

          {/* AwardWallet Parsed Confirmations (JSON Output) */}
          {log.awardwallet_parsed_confirmations && log.awardwallet_parsed_confirmations.length > 0 && renderCollapsibleSection(
            log.id,
            'AwardWallet Parsed Confirmations (JSON)',
            <Award className="w-4 h-4 text-amber-600" />,
            <div className="space-y-4">
              {log.awardwallet_parsed_confirmations.map((conf, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-amber-50 px-4 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                        AwardWallet
                      </span>
                      <span className="text-sm font-medium text-gray-700">
                        {conf.data?.document_type || 'Unknown Type'}
                      </span>
                      {conf.data?.confirmation_number && (
                        <span className="text-xs text-gray-500">
                          #{conf.data.confirmation_number}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => copyToClipboard(JSON.stringify(conf.data, null, 2), `aw-parsed-${log.id}-${idx}`)}
                      className="p-1.5 rounded bg-white hover:bg-gray-200 transition-colors"
                      title="Copy JSON"
                    >
                      {copiedId === `aw-parsed-${log.id}-${idx}` ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-600" />
                      )}
                    </button>
                  </div>
                  <pre className="bg-gray-900 text-gray-100 p-4 text-xs overflow-x-auto max-h-64">
                    {JSON.stringify(conf.data, null, 2)}
                  </pre>
                </div>
              ))}
            </div>
          )}

          {/* Parsing Comparison - Field Mismatches */}
          {log.parsing_comparison && renderCollapsibleSection(
            log.id,
            `Parsing Comparison (${log.parsing_comparison.match_percentage}% Match)`,
            <GitCompare className="w-4 h-4 text-blue-600" />,
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-green-50 p-3 rounded-lg">
                  <div className="text-xs text-green-600 mb-1">Match Rate</div>
                  <div className="text-xl font-bold text-green-700">{log.parsing_comparison.match_percentage}%</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-xs text-gray-500 mb-1">AI Confirmations</div>
                  <div className="text-xl font-bold text-gray-900">{log.parsing_comparison.ai_confirmation_count}</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-xs text-gray-500 mb-1">AW Confirmations</div>
                  <div className="text-xl font-bold text-gray-900">{log.parsing_comparison.aw_confirmation_count}</div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-xs text-gray-500 mb-1">Matching Fields</div>
                  <div className="text-xl font-bold text-gray-900">{log.parsing_comparison.matching_fields?.length || 0}</div>
                </div>
              </div>

              {/* Mismatched Fields */}
              {log.parsing_comparison.mismatched_fields?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-red-800 flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-4 h-4" />
                    Field Mismatches ({log.parsing_comparison.mismatched_fields.length})
                  </h4>
                  <div className="space-y-2">
                    {log.parsing_comparison.mismatched_fields.map((mismatch, idx) => (
                      <div key={idx} className="bg-white rounded-lg p-3 border border-red-100">
                        <div className="font-medium text-gray-700 text-sm mb-2">{mismatch.field}</div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="bg-indigo-50 p-2 rounded">
                            <span className="text-indigo-600 font-medium">AI:</span>
                            <span className="ml-1 text-gray-700">{JSON.stringify(mismatch.ai_value)}</span>
                          </div>
                          <div className="bg-amber-50 p-2 rounded">
                            <span className="text-amber-600 font-medium">AW:</span>
                            <span className="ml-1 text-gray-700">{JSON.stringify(mismatch.aw_value)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Leg Mismatches */}
              {log.parsing_comparison.legs_comparison?.leg_mismatches?.length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-orange-800 flex items-center gap-2 mb-3">
                    <Plane className="w-4 h-4" />
                    Leg Mismatches
                  </h4>
                  <div className="space-y-3">
                    {log.parsing_comparison.legs_comparison.leg_mismatches.map((legMismatch, idx) => (
                      <div key={idx} className="bg-white rounded-lg p-3 border border-orange-100">
                        <div className="font-medium text-gray-700 text-sm mb-2">Leg {legMismatch.leg_number}</div>
                        <div className="space-y-2">
                          {legMismatch.mismatches.map((m, mIdx) => (
                            <div key={mIdx} className="grid grid-cols-3 gap-2 text-xs">
                              <div className="font-medium text-gray-600">{m.field}</div>
                              <div className="bg-indigo-50 p-1 rounded text-center">
                                <span className="text-indigo-700">{m.ai_value}</span>
                              </div>
                              <div className="bg-amber-50 p-1 rounded text-center">
                                <span className="text-amber-700">{m.aw_value}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Only Fields */}
              {log.parsing_comparison.ai_only_fields?.length > 0 && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-indigo-800 mb-2">
                    Fields Only in AI Result ({log.parsing_comparison.ai_only_fields.length})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {log.parsing_comparison.ai_only_fields.map((f, idx) => (
                      <span key={idx} className="px-2 py-1 bg-white rounded text-xs text-indigo-700 border border-indigo-200">
                        {f.field}: {JSON.stringify(f.value)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* AwardWallet Only Fields */}
              {log.parsing_comparison.aw_only_fields?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-amber-800 mb-2">
                    Fields Only in AwardWallet Result ({log.parsing_comparison.aw_only_fields.length})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {log.parsing_comparison.aw_only_fields.map((f, idx) => (
                      <span key={idx} className="px-2 py-1 bg-white rounded text-xs text-amber-700 border border-amber-200">
                        {f.field}: {JSON.stringify(f.value)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Matching Fields */}
              {log.parsing_comparison.matching_fields?.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-green-800 mb-2">
                    Matching Fields ({log.parsing_comparison.matching_fields.length})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {log.parsing_comparison.matching_fields.map((field, idx) => (
                      <span key={idx} className="px-2 py-1 bg-white rounded text-xs text-green-700 border border-green-200">
                        {field}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* AwardWallet Parsing Results */}
          {log.awardwallet_parsing && renderCollapsibleSection(
            log.id,
            'AwardWallet Parsing Results',
            <Award className="w-4 h-4 text-amber-600" />,
            <div className="space-y-4">
              {/* Status Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-xs text-gray-500 mb-1">Status</div>
                  <div className={`text-sm font-medium ${log.awardwallet_parsing.error ? 'text-red-600' : log.awardwallet_parsing.success ? 'text-green-600' : 'text-gray-600'}`}>
                    {log.awardwallet_parsing.error ? 'Error' : log.awardwallet_parsing.success ? 'Success' : 'No Results'}
                  </div>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-xs text-gray-500 mb-1">Confirmations Found</div>
                  <div className="text-sm font-medium text-gray-900">{log.awardwallet_parsing.confirmations_count || 0}</div>
                </div>
                {log.awardwallet_parsing.duration_ms && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1">Duration</div>
                    <div className="text-sm font-medium text-gray-900">{log.awardwallet_parsing.duration_ms}ms</div>
                  </div>
                )}
                {log.awardwallet_parsing.comparison_metrics && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="text-xs text-gray-500 mb-1">Comparison</div>
                    <div className="text-sm font-medium text-gray-900">
                      {log.awardwallet_parsing.comparison_metrics.both_parsed ? 'Both Parsed' :
                       log.awardwallet_parsing.comparison_metrics.ai_only ? 'AI Only' :
                       log.awardwallet_parsing.comparison_metrics.aw_only ? 'AW Only' : 'N/A'}
                    </div>
                  </div>
                )}
              </div>

              {/* Error Message */}
              {log.awardwallet_parsing.error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="text-xs text-red-600 font-medium mb-1">Error Message</div>
                  <div className="text-sm text-red-700">{log.awardwallet_parsing.error}</div>
                </div>
              )}

              {/* Parsed Data */}
              {log.awardwallet_parsing.data && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">AwardWallet Parsed Data</h4>
                  {Array.isArray(log.awardwallet_parsing.data) && log.awardwallet_parsing.data.length > 0 ? (
                    log.awardwallet_parsing.data.map((conf, idx) => (
                      <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden mb-2">
                        <div className="bg-amber-50 px-4 py-2 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                              AwardWallet
                            </span>
                            <span className="text-sm font-medium text-gray-700">
                              {conf.type || conf.document_type || 'Confirmation'}
                            </span>
                            {conf.confirmation_number && (
                              <span className="text-xs text-gray-500">
                                #{conf.confirmation_number}
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => copyToClipboard(JSON.stringify(conf, null, 2), `aw-conf-${log.id}-${idx}`)}
                            className="p-1.5 rounded bg-white hover:bg-gray-200 transition-colors"
                            title="Copy JSON"
                          >
                            {copiedId === `aw-conf-${log.id}-${idx}` ? (
                              <Check className="w-4 h-4 text-green-600" />
                            ) : (
                              <Copy className="w-4 h-4 text-gray-600" />
                            )}
                          </button>
                        </div>
                        <pre className="bg-gray-900 text-gray-100 p-4 text-xs overflow-x-auto max-h-64">
                          {JSON.stringify(conf, null, 2)}
                        </pre>
                      </div>
                    ))
                  ) : (
                    renderJsonViewer(log.awardwallet_parsing.data, `aw-data-${log.id}`)
                  )}
                </div>
              )}

              {/* Comparison Metrics */}
              {log.awardwallet_parsing.comparison_metrics && Object.keys(log.awardwallet_parsing.comparison_metrics).length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Comparison Metrics</h4>
                  {renderJsonViewer(log.awardwallet_parsing.comparison_metrics, `aw-metrics-${log.id}`)}
                </div>
              )}
            </div>
          )}

          {/* Parsed Data (for document imports) */}
          {isDocumentImport && log.parsed_data && renderCollapsibleSection(
            log.id,
            'Raw Parsed Data',
            <FileJson className="w-4 h-4 text-cyan-600" />,
            renderJsonViewer(log.parsed_data, `parsed-${log.id}`)
          )}

          {/* Full Log Data (for debugging) */}
          {renderCollapsibleSection(
            log.id,
            'Full Log Data (Debug)',
            <FileJson className="w-4 h-4 text-orange-600" />,
            renderJsonViewer(log, `full-${log.id}`)
          )}
        </div>
      </div>
    );
  };

  if (loading && logs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-red-800 mb-2">Error Loading Logs</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={fetchLogs}
          className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileJson className="w-7 h-7" />
            Parsing Log
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            View AI parsing results from emails and document imports
          </p>
        </div>
        <div className="flex gap-2">
          {/* Source Filter */}
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Sources</option>
            <option value="email">Email Only</option>
            <option value="document_import">Document Import Only</option>
          </select>
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value={25}>Show 25</option>
            <option value={50}>Show 50</option>
            <option value={100}>Show 100</option>
            <option value={200}>Show 200</option>
          </select>
          <button
            onClick={fetchLogs}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="text-sm text-gray-600">
          Showing <span className="font-semibold">{logs.length}</span> of{' '}
          <span className="font-semibold">{total}</span> parsing logs
          {sourceFilter && (
            <span className="ml-2 text-gray-400">
              (filtered to {sourceFilter === 'email' ? 'emails' : 'document imports'})
            </span>
          )}
        </div>
      </div>

      {/* Logs List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {logs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <FileJson className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg">No parsing logs yet</p>
            <p className="text-sm mt-2">
              {sourceFilter
                ? `No ${sourceFilter === 'email' ? 'email' : 'document import'} logs found`
                : 'Emails and document imports will appear here with their parsed content'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {logs.map((log) => (
              <div key={log.id} className="hover:bg-gray-50">
                {/* Log Header - Clickable */}
                <div
                  className="p-4 cursor-pointer"
                  onClick={() => toggleLogExpansion(log.id)}
                >
                  <div className="flex items-start justify-between">
                    {renderLogHeader(log)}

                    {/* Expand/Collapse Arrow */}
                    <div className="ml-4 flex-shrink-0">
                      {expandedLogId === log.id ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedLogId === log.id && renderExpandedDetails(log)}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer Stats */}
      {logs.length > 0 && logs.length < total && (
        <div className="text-center text-sm text-gray-500">
          <p>Showing {logs.length} of {total} logs. Increase the limit to see more.</p>
        </div>
      )}

      {/* Parsing feedback modal (rendered via portal) */}
      {renderFeedbackModal()}
    </div>
  );
};

export default ParsingLogs;
